/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RoleProfileSelect.tsx
 🗓️ Created: 2025-01-10
 🧩 Description:
    Custom role profile selection component with search functionality,
    for selecting user role profiles with descriptions.

 🔧 Notes:
    - Implements searchable dropdown with search functionality
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Shows profile descriptions as tooltips
    - Optimized for performance with filtered results
    - Integrates with form validation systems

 🔁 Last Updated: 2025-01-10 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback, memo } from 'react';
import { ChevronDown, Search, Shield } from 'lucide-react';

interface RoleProfileOption {
  id: string;
  name: string;
  description?: string;
}

interface RoleProfileSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: RoleProfileOption[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
}

export const RoleProfileSelect: React.FC<RoleProfileSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
  placeholder = 'Select role profile...',
  disabled = false,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.name.toLowerCase().includes(query) ||
      (option.description && option.description.toLowerCase().includes(query))
    );
  }, [searchQuery, options]);

  // Get selected option label
  const selectedLabel = useMemo(() => {
    const selected = options.find(option => option.id === value);
    return selected ? selected.name : placeholder;
  }, [value, placeholder, options]);

  // Handle selection (memoized)
  const handleSelect = useCallback((id: string) => {
    onChange(id);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange]);

  // Handle keyboard (memoized)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  }, []);

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {loading ? 'Loading role profiles...' : selectedLabel}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search role profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                No role profiles found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option.id)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === option.id ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate font-medium">{option.name}</span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground truncate">{option.description}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {filteredOptions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredOptions.length} role profile{filteredOptions.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default memo(RoleProfileSelect);

